import axios from 'axios';
import { aadConfig, getAccessToken } from '../aad';
import type { ProjectContent } from '../dto/content';
import type { Patch } from '../dto/common';
import type { ProjectContentCategoryCount } from '../dto/projectShare';
import apiProxy from './apiProxy';

/** Fetch content as raw buffer. */
export const getContent = apiProxy(
  'getContent',
  async (url: string): Promise<{ data: ArrayBuffer; contentType: string }> => {
    // await getAccessToken();

    const axiosEx = axios.create();
    axiosEx.defaults.headers.common = {};

    const resp = await axiosEx.get(url, { responseType: 'arraybuffer' });
    return {
      data: resp.data,
      contentType: resp.headers['content-type'] || 'application/octet-stream',
    };
  },
);

/** Fetch project content list. */
export const getProjectContent = apiProxy(
  'getProjectContent',
  async (accountId: string, projectId: string, phaseId?: string, category?: string): Promise<ProjectContent[]> => {
    await getAccessToken(aadConfig().contentScopes);

    const q: string[] = [];
    if (phaseId) q.push(`phaseId=${phaseId}`);
    if (category) q.push(`category=${category}`);

    const { data } = await axios.get(
      `${process.env.CONTENT_API}/accounts/${accountId}/projects/${projectId}${q.length > 0 ? `?${q.join('&')}` : ''}`,
    );
    return data;
  },
);

/** Fetch project content category counts list. */
export const getProjectContentCounts = apiProxy(
  'getProjectContentCounts',
  async (accountId: string, projectId: string): Promise<ProjectContentCategoryCount[]> => {
    await getAccessToken(aadConfig().contentScopes);

    const { data } = await axios.get(
      `${process.env.CONTENT_API}/accounts/${accountId}/projects/${projectId}/category-counts`,
    );
    return data;
  },
);

/** One sheet of a drawing set, as the pipeline has read it. */
export type DrawingSheet = {
  id?: string;
  drawingId?: string;
  drawingName?: string;
  pageName?: string;
  pageNo: number;
  /** The number printed on the sheet. Null until the per-sheet pass has read it. */
  sheetNumber?: string;
  /** The sheet's own title. Null until the per-sheet pass has read it. */
  title?: string;
  code?: string;
  sub?: string;
  /** What the platform acts on: a person's choice where one was made. */
  discipline?: string;
  /** What the classifier read, kept separate from discipline. */
  classification?: string;
  score?: number;
  setByAPerson?: boolean;
  /** Whether the per-sheet extraction pass has read this sheet. */
  extracted?: boolean;
  /** Pay-item rows read off a Summary of Quantities sheet, where this sheet is one. */
  quantities?: DrawingSheetQuantity[];
};

/** One pay-item row read off a Summary of Quantities sheet. */
export type DrawingSheetQuantity = {
  payItem?: string;
  description?: string;
  unit?: string;
  quantity?: number;
  csi?: string;
};

/** The sheets of a phase's drawing sets, with what has and has not been read. */
export const getDrawingSheets = apiProxy(
  'getDrawingSheets',
  async (accountId: string, projectId: string, phaseId: string): Promise<DrawingSheet[]> => {
    await getAccessToken(aadConfig().contentScopes);

    const { data } = await axios.get(
      `${process.env.CONTENT_API}/accounts/${accountId}/projects/${projectId}/drawing-sheets?phaseId=${encodeURIComponent(phaseId)}`,
    );
    return data ?? [];
  },
);

/**
 * Queues the per-sheet extraction pass for a phase's drawings.
 *
 * Fans out one message per sheet, so a large set finishes in the time of its
 * slowest sheet rather than the sum of all of them. Returns how many sheets were
 * queued; the work then runs in the background and the page re-reads progress.
 */
export const resummarizeDrawingSheets = apiProxy(
  'resummarizeDrawingSheets',
  async (
    accountId: string,
    projectId: string,
    phaseId: string,
    all = false,
  ): Promise<{ queued: number; sheets: number; skipped: number }> => {
    await getAccessToken(aadConfig().contentScopes);

    const { data } = await axios.post(
      `${process.env.CONTENT_API}/accounts/${accountId}/projects/${projectId}/drawing-sheets/resummarize?phaseId=${encodeURIComponent(phaseId)}&all=${all}`,
    );
    return data;
  },
);

/** Patch a content record (e.g. store takeoff packages on a drawing's metadata). */
export const patchProjectContent = apiProxy(
  'patchProjectContent',
  async (accountId: string, projectId: string, contentId: string, patch: Patch[]): Promise<ProjectContent> => {
    await getAccessToken(aadConfig().contentScopes);
    const { data } = await axios.patch(
      `${process.env.CONTENT_API}/accounts/${accountId}/projects/${projectId}/${encodeURIComponent(contentId)}`,
      patch,
    );
    return data;
  },
);
